/**
 * v3.18-1314 — Ghost Operator Console
 *
 * Terminal-style command surface for internal operators, embedded inside
 * the single existing Ghost panel.  Adds:
 *
 *   * Locked-Client Command Header (pinned banner)
 *   * Autocomplete with usage-frequency ranking (personalizes over time)
 *   * Fuzzy client suggestions as the operator types a name
 *   * Command history (last 25, per user, localStorage)
 *   * Keyboard-first navigation:
 *       Enter          — execute / accept suggestion
 *       Tab            — accept highlighted suggestion
 *       ↑ / ↓          — navigate suggestions OR history (when list empty)
 *       Esc            — dismiss suggestions / clear pending
 *
 * The console DOES NOT own state — it is fully controlled by
 * `GhostFloatingIcon.jsx` which owns the chat message stream, session
 * state, and API calls.  This file is a pure UI/UX shell.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import api from '../lib/api';
import { Terminal, X as CloseIcon } from 'lucide-react';

// ── Local-storage keys (per user) ───────────────────────────────────
const HIST_KEY = (uid) => `ghost-cmd-history-${uid || 'anon'}`;
const FREQ_KEY = (uid) => `ghost-cmd-frequency-${uid || 'anon'}`;

// ── Approved v1 command templates ──────────────────────────────────
// Placeholder tokens follow the pattern `<..>` — after the operator
// picks a template, the cursor lands on the first placeholder.
export const COMMAND_TEMPLATES = [
  { id: 'mark_active',       label: 'mark <client> active',              text: 'mark <client> active' },
  { id: 'mark_inactive',     label: 'mark <client> inactive',            text: 'mark <client> inactive' },
  { id: 'reactivate',        label: 'reactivate <client>',               text: 'reactivate <client>' },
  { id: 'deactivate',        label: 'deactivate <client>',               text: 'deactivate <client>' },
  { id: 'archive',           label: 'archive <client>',                  text: 'archive <client>' },
  { id: 'update_phone',      label: 'update phone to <value>',           text: 'update phone to <value>' },
  { id: 'update_email',      label: 'update email to <value>',           text: 'update email to <value>' },
  { id: 'update_mobile',     label: 'update mobile to <value>',          text: 'update mobile to <value>' },
  { id: 'update_address',    label: 'update address to <value>',         text: 'update address to <value>' },
  { id: 'update_dob',        label: 'update dob to <value>',             text: 'update dob to <value>' },
  { id: 'update_employer',   label: 'update employer to <value>',        text: 'update employer to <value>' },
  { id: 'update_occupation', label: 'update occupation to <value>',      text: 'update occupation to <value>' },
  { id: 'update_income',     label: 'update income to <value>',          text: 'update income to <value>' },
  { id: 'add_note',          label: 'add note: <text>',                  text: 'add note: <text>' },
  { id: 'clear_context',     label: 'clear context',                     text: 'clear context' },
];

// ── Persistence helpers ────────────────────────────────────────────

const loadHistory = (uid) => {
  try {
    const raw = localStorage.getItem(HIST_KEY(uid));
    return raw ? JSON.parse(raw).slice(0, 25) : [];
  } catch { return []; }
};

const pushHistory = (uid, entry) => {
  try {
    const cur = loadHistory(uid).filter(x => x !== entry);
    const next = [entry, ...cur].slice(0, 25);
    localStorage.setItem(HIST_KEY(uid), JSON.stringify(next));
  } catch { /* ignore */ }
};

const loadFreq = (uid) => {
  try {
    return JSON.parse(localStorage.getItem(FREQ_KEY(uid)) || '{}');
  } catch { return {}; }
};

const bumpFreq = (uid, templateId) => {
  try {
    const f = loadFreq(uid);
    f[templateId] = (f[templateId] || 0) + 1;
    localStorage.setItem(FREQ_KEY(uid), JSON.stringify(f));
  } catch { /* ignore */ }
};

// ── Client header (pinned banner) ──────────────────────────────────

export function LockedClientHeader({ client, onClear, operatorTeams }) {
  if (!client) return null;
  const deal = client.current_deal;
  const dealStr = deal
    ? [deal.stage || '—',
       deal.amount ? `$${Number(deal.amount).toLocaleString('en-US')}` : ''].filter(Boolean).join(' · ')
    : '—';
  const teams = Array.isArray(operatorTeams) ? operatorTeams : [];
  return (
    <div className="bg-slate-950 text-emerald-300 border-b border-emerald-800/50 font-mono px-3 py-2 text-[11px] space-y-0.5"
         data-testid="ghost-locked-client-header">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-emerald-400">●</span>
          <span className="uppercase tracking-wider text-emerald-500 text-[9px]">Locked</span>
          <span className="text-white font-semibold">{client.full_name}</span>
        </div>
        <button
          onClick={onClear}
          className="text-slate-400 hover:text-white uppercase tracking-wider text-[9px] px-1.5 py-0.5 border border-slate-700 rounded hover:border-slate-500"
          data-testid="ghost-operator-clear-ctx"
          title="Clear locked client (Esc twice)">
          Clear
        </button>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] pl-4">
        <div><span className="text-slate-500 uppercase mr-1">ID</span><span className="text-slate-200">{client.record || client.id?.slice(0, 8)}</span></div>
        <div><span className="text-slate-500 uppercase mr-1">Company</span><span className="text-slate-200">{client.company_name || '—'}</span></div>
        <div><span className="text-slate-500 uppercase mr-1">Status</span><span className="text-slate-200">{client.status || '—'}</span></div>
        <div><span className="text-slate-500 uppercase mr-1">Deal</span><span className="text-slate-200">{dealStr}</span></div>
      </div>
      {teams.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap pl-4 pt-1 border-t border-emerald-900/60 mt-1"
             data-testid="ghost-operator-teams-locked">
          <span className="text-slate-500 uppercase text-[9px] mr-0.5">You:</span>
          {teams.map(t => (
            <span key={t.id}
                  className={`text-[9px] px-1.5 py-0.5 rounded border ${
                    t.team_type === 'vo' ? 'bg-indigo-950 border-indigo-700 text-indigo-200'
                  : t.team_type === 'sales' ? 'bg-blue-950 border-blue-700 text-blue-200'
                  : t.team_type === 'concierge' ? 'bg-teal-950 border-teal-700 text-teal-200'
                  : t.team_type === 'funding' ? 'bg-amber-950 border-amber-700 text-amber-200'
                  : t.team_type === 'applications' ? 'bg-violet-950 border-violet-700 text-violet-200'
                  :                         'bg-slate-800 border-slate-600 text-slate-200'
                  }`}
                  title={`${t.team_type_label}${t.is_lead ? ' — Team Lead' : ''}`}
                  data-testid={`ghost-operator-team-${t.id}`}>
              {t.is_lead ? '★ ' : ''}{t.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Autocomplete + history-aware input ──────────────────────────────

/**
 * Controlled command input with autocomplete, history, and keyboard-first UX.
 *
 * Props:
 *   userId          — used to key localStorage buckets.
 *   value           — current text (controlled).
 *   onChange(v)     — text change.
 *   onSubmit(v)     — Enter pressed; parent handles execution.
 *   disabled        — send in-flight.
 *   locked          — bool; when true we skip fuzzy client lookups.
 *   placeholder     — string.
 */
export function OperatorInput({ userId, value, onChange, onSubmit, disabled,
                                locked, placeholder }) {
  const inputRef = useRef(null);
  const [history, setHistory]         = useState(() => loadHistory(userId));
  const [histIdx, setHistIdx]         = useState(-1);            // -1 = "new"
  const [freq, setFreq]               = useState(() => loadFreq(userId));
  const [clientMatches, setClientMatches] = useState([]);
  const [highlight, setHighlight]     = useState(0);
  const [fetchingClients, setFetchingClients] = useState(false);
  // Suggestions are re-derived every render from ``value`` + ``freq`` +
  // ``history`` + ``clientMatches``.
  const suggestions = useMemo(() => {
    const v = (value || '').trim().toLowerCase();
    if (!v) return [];
    // 1) History matches (prefix)
    const histMatches = (history || [])
      .filter(h => h.toLowerCase().startsWith(v))
      .slice(0, 3)
      .map(h => ({ kind: 'history', label: h, text: h }));
    // 2) Command templates ranked by usage frequency
    const tmplMatches = COMMAND_TEMPLATES
      .filter(t => t.label.toLowerCase().includes(v)
                    || t.text.toLowerCase().startsWith(v))
      .map(t => ({ ...t, kind: 'template', score: freq[t.id] || 0 }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 6);
    // 3) Fuzzy client matches (only when NOT locked to a client — locking a
    //    new client from the suggestion drops the old lock automatically).
    const cliMatches = (clientMatches || []).map(c => ({
      kind: 'client', client: c,
      label: `${c.full_name}  ·  ${c.record || (c.id || '').slice(0, 8)}${c.status ? '  ·  ' + c.status : ''}`,
      text: c.full_name,
    }));
    return [...histMatches, ...tmplMatches, ...cliMatches];
  }, [value, freq, history, clientMatches]);

  // Debounced fuzzy client fetch — only when the text looks like a
  // name-only utterance (no command verbs and no identifiers).
  useEffect(() => {
    const v = (value || '').trim();
    if (v.length < 2) { setClientMatches([]); return; }
    if (/\b(mark|set|make|change|move|put|reactivate|activate|deactivate|archive|reinstate|update|correct|add|clear|note)\b/i.test(v)) {
      setClientMatches([]);
      return;
    }
    if (/\b(EQF|APP|APPLICATION|CONF|CONFIRMATION|DEAL)[-\s]?[A-Z0-9]{4,}\b/i.test(v)) {
      setClientMatches([]);
      return;
    }
    let cancelled = false;
    setFetchingClients(true);
    const handle = setTimeout(async () => {
      try {
        const r = await api.get('/ghost/command-engine/suggest',
                                { params: { q: v, kind: 'client' } });
        if (!cancelled) setClientMatches(r.data?.items || []);
      } catch { if (!cancelled) setClientMatches([]); }
      finally  { if (!cancelled) setFetchingClients(false); }
    }, 180);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [value]);

  // Reset highlight whenever suggestion list content changes.
  useEffect(() => { setHighlight(0); }, [suggestions.length]);

  // Fill a template at the first placeholder cursor position.
  const applySuggestion = useCallback((s) => {
    let text = s.text;
    let caret = text.length;
    if (s.kind === 'template') {
      // Cursor lands on the first `<placeholder>` token.
      const idx = text.indexOf('<');
      if (idx >= 0) {
        const end = text.indexOf('>', idx);
        if (end > 0) {
          text = text.slice(0, idx) + text.slice(end + 1);
          caret = idx;
        }
      }
      bumpFreq(userId, s.id);
      setFreq(loadFreq(userId));
    }
    if (s.kind === 'client') {
      // Prefix the client name so the operator can then type a verb.
      text = s.client.full_name + ' ';
      caret = text.length;
    }
    onChange(text);
    setHistIdx(-1);
    // Restore focus and position the caret.
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        try { inputRef.current.setSelectionRange(caret, caret); } catch { /* ignore */ }
      }
    });
  }, [userId, onChange]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (suggestions.length && highlight >= 0 && highlight < suggestions.length) {
        const s = suggestions[highlight];
        // Templates with placeholders → fill and wait for the operator
        // to complete.  History / clear-context / clients that already
        // have no placeholder → execute directly.
        if (s.kind === 'template' && s.text.includes('<')) {
          applySuggestion(s);
          return;
        }
        applySuggestion(s);
        // For history and finished templates → auto-submit.
        if (s.kind === 'history') {
          onSubmit(s.text);
          pushHistory(userId, s.text);
          setHistory(loadHistory(userId));
          setHistIdx(-1);
          return;
        }
      }
      const v = (value || '').trim();
      if (!v || disabled) return;
      onSubmit(v);
      pushHistory(userId, v);
      setHistory(loadHistory(userId));
      setHistIdx(-1);
      return;
    }
    if (e.key === 'Escape') {
      // Clear suggestions + fuzzy matches first.
      if (clientMatches.length || suggestions.length) {
        setClientMatches([]);
        onChange('');
        e.preventDefault();
        return;
      }
    }
    if (e.key === 'Tab') {
      if (suggestions.length && highlight >= 0 && highlight < suggestions.length) {
        e.preventDefault();
        applySuggestion(suggestions[highlight]);
        return;
      }
    }
    if (e.key === 'ArrowDown') {
      if (suggestions.length) {
        e.preventDefault();
        setHighlight(h => Math.min(h + 1, suggestions.length - 1));
        return;
      }
      // No suggestions → walk history forward (toward newest).
      if (history.length) {
        e.preventDefault();
        const next = Math.max(histIdx - 1, -1);
        setHistIdx(next);
        onChange(next === -1 ? '' : history[next]);
      }
      return;
    }
    if (e.key === 'ArrowUp') {
      if (suggestions.length) {
        e.preventDefault();
        setHighlight(h => Math.max(h - 1, 0));
        return;
      }
      if (history.length) {
        e.preventDefault();
        const next = Math.min(histIdx + 1, history.length - 1);
        setHistIdx(next);
        onChange(history[next]);
      }
      return;
    }
  };

  return (
    <div className="relative" data-testid="ghost-operator-input-wrap">
      {/* Suggestions dropdown */}
      {suggestions.length > 0 && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-slate-950 border border-emerald-800/60 rounded-md shadow-lg overflow-hidden text-[11px] font-mono z-50"
             data-testid="ghost-operator-autocomplete">
          {suggestions.map((s, i) => {
            const active = i === highlight;
            return (
              <button
                key={`${s.kind}-${s.id || s.label || i}`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => {
                  applySuggestion(s);
                  if (s.kind === 'history') { onSubmit(s.text); }
                }}
                className={`w-full text-left px-2 py-1 flex items-center gap-2 ${
                  active ? 'bg-emerald-900/50 text-white' : 'text-slate-200 hover:bg-slate-900'
                }`}
                data-testid={`ghost-suggestion-${i}`}>
                <span className={`inline-block w-12 text-[9px] uppercase tracking-wider ${
                  s.kind === 'history' ? 'text-amber-400' :
                  s.kind === 'client'  ? 'text-blue-400'  : 'text-emerald-400'
                }`}>{s.kind}</span>
                <span className="flex-1 truncate">{s.label}</span>
                {s.kind === 'template' && s.score > 0 && (
                  <span className="text-slate-500 text-[9px]">×{s.score}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* PowerShell-style prompt line */}
      <div className="flex items-center gap-2 bg-slate-950 rounded border border-emerald-800/60 focus-within:border-emerald-500 px-2 py-1.5 font-mono">
        <Terminal className="h-3 w-3 text-emerald-400 shrink-0" />
        <span className="text-emerald-400 text-[11px] shrink-0">ghost&gt;</span>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={e => { onChange(e.target.value); setHistIdx(-1); }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || 'Command Ghost (e.g. Mark John Doe Active)'}
          className="flex-1 bg-transparent text-slate-100 placeholder:text-slate-600 text-[12px] outline-none font-mono"
          disabled={disabled}
          spellCheck={false}
          autoComplete="off"
          data-testid="ghost-chat-input"
        />
        {fetchingClients && <span className="text-[9px] text-slate-500 shrink-0">…</span>}
      </div>
    </div>
  );
}

export default OperatorInput;
